import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, UserPlus, Trash2, Star, Upload, FileSpreadsheet, Plus } from "lucide-react";

interface Group {
  id: string;
  name: string;
  semester: string;
  class: string;
  leader_student_id: string | null;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  student_number: string;
}

interface MemberRow {
  id: string;
  student_id: string;
  students: Student | null;
}

export function FieldGroupRoster({ group, onChanged }: { group: Group; onChanged?: () => void }) {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [adding, setAdding] = useState(false);

  async function loadMembers() {
    const { data, error } = await supabase
      .from("field_group_members")
      .select("id, student_id, students(id, first_name, last_name, student_number)")
      .eq("group_id", group.id);
    if (error) { toast.error(error.message); return; }
    setMembers((data ?? []) as any);
  }

  async function loadClassStudents() {
    const { data, error } = await supabase
      .from("students")
      .select("id, first_name, last_name, student_number")
      .eq("class", group.class)
      .order("last_name", { ascending: true })
      .order("first_name", { ascending: true });
    if (error) { toast.error(error.message); return; }
    setClassStudents((data ?? []) as Student[]);
  }

  useEffect(() => {
    setSelected(new Set());
    setFilter("");
    loadMembers();
    loadClassStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id, group.class]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.student_id)), [members]);

  const available = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const list = classStudents.filter((s) => !memberIds.has(s.id));
    if (!f) return list;
    return list.filter((s) =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(f) ||
      (s.student_number || "").toLowerCase().includes(f)
    );
  }, [classStudents, memberIds, filter]);

  async function add(s: Student) {
    setAdding(true);
    const { error } = await supabase.from("field_group_members").insert({ group_id: group.id, student_id: s.id });
    setAdding(false);
    if (error) { toast.error(error.message); return; }
    loadMembers();
    onChanged?.();
  }

  async function addSelected() {
    const ids = Array.from(selected).filter((id) => !memberIds.has(id));
    if (ids.length === 0) { toast.info("No students selected"); return; }
    setAdding(true);
    const { error } = await supabase
      .from("field_group_members")
      .insert(ids.map((student_id) => ({ group_id: group.id, student_id })));
    setAdding(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`Added ${ids.length} student(s)`);
    setSelected(new Set());
    loadMembers();
    onChanged?.();
  }

  async function remove(m: MemberRow) {
    const { error } = await supabase.from("field_group_members").delete().eq("id", m.id);
    if (error) { toast.error(error.message); return; }
    loadMembers();
    onChanged?.();
  }

  async function setLeader(student_id: string) {
    const { error } = await supabase.from("field_groups").update({ leader_student_id: student_id }).eq("id", group.id);
    if (error) { toast.error(error.message); return; }
    toast.success("Leader updated");
    onChanged?.();
  }

  async function downloadTemplate() {
    const XLSX = await import("xlsx");
    const rows = classStudents.length > 0
      ? classStudents.map((s) => ({ "Student Number": s.student_number, "Group Name": group.name }))
      : [{ "Student Number": "", "Group Name": group.name }];
    const ws = XLSX.utils.json_to_sheet(rows, { header: ["Student Number", "Group Name"] });
    ws["!cols"] = [{ wch: 18 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Roster");
    const meta = XLSX.utils.aoa_to_sheet([
      ["Group", group.name],
      ["Semester", group.semester],
      ["Class", group.class],
      ["Notes", "Add one row per student. Group Name must match exactly."],
    ]);
    XLSX.utils.book_append_sheet(wb, meta, "Metadata");
    XLSX.writeFile(wb, `Roster_${group.name.replace(/[^a-z0-9]+/gi, "_")}.xlsx`);
  }

  async function handleBulkUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoadingBulk(true);
    try {
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<any>(ws);
      const studentNumbers = rows
        .map((r) => String(r["Student Number"] ?? r["student_number"] ?? "").trim())
        .filter(Boolean);
      if (studentNumbers.length === 0) {
        toast.error("No 'Student Number' values found");
        return;
      }
      const { data: students } = await supabase
        .from("students")
        .select("id, student_number, class")
        .in("student_number", studentNumbers);
      const found = (students ?? []) as Array<{ id: string; student_number: string; class: string | null }>;
      const matched = found.filter((s) => s.class === group.class);
      if (matched.length === 0) {
        toast.error("No matching students found in this class");
        return;
      }
      const toInsert = matched
        .filter((s) => !memberIds.has(s.id))
        .map((s) => ({ group_id: group.id, student_id: s.id }));
      if (toInsert.length === 0) {
        toast.info("All matched students already in this group");
        return;
      }
      const { error } = await supabase.from("field_group_members").insert(toInsert);
      if (error) { toast.error(error.message); return; }
      toast.success(`Added ${toInsert.length} student(s)`);
      loadMembers();
      onChanged?.();
    } finally {
      setLoadingBulk(false);
      e.target.value = "";
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAllVisible() {
    const visibleIds = available.map((s) => s.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  const allVisibleSelected = available.length > 0 && available.every((s) => selected.has(s.id));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Roster — {group.name}</CardTitle>
        <CardDescription>{group.class} • Semester {group.semester} • {members.length} member(s)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <FileSpreadsheet className="w-3 h-3 mr-1" /> Download template
          </Button>
          <label className="inline-flex">
            <input type="file" accept=".xlsx,.xls" hidden onChange={handleBulkUpload} disabled={loadingBulk} />
            <Button variant="outline" size="sm" asChild disabled={loadingBulk}>
              <span><Upload className="w-3 h-3 mr-1" /> {loadingBulk ? "Uploading…" : "Bulk upload"}</span>
            </Button>
          </label>
        </div>

        {/* Available students from class */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Label className="text-xs">
              Available students in {group.class}{" "}
              <span className="text-muted-foreground font-normal">
                ({available.length} available • {members.length} in roster)
              </span>
            </Label>
            <Button
              size="sm"
              onClick={addSelected}
              disabled={selected.size === 0 || adding}
            >
              <UserPlus className="w-3 h-3 mr-1" /> Add {selected.size} selected
            </Button>
          </div>

          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder={`Filter by name or reg #…`}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          <div className="border rounded-md max-h-72 overflow-auto">
            {classStudents.length === 0 ? (
              <div className="p-4 text-sm text-center text-muted-foreground">
                No students are registered in {group.class}. Add students from User Management → Students first.
              </div>
            ) : available.length === 0 ? (
              <div className="p-4 text-sm text-center text-muted-foreground">
                {filter
                  ? "No matches for that filter."
                  : `All ${group.class} students are already in this group.`}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleSelectAllVisible}
                        aria-label="Select all visible"
                      />
                    </TableHead>
                    <TableHead className="w-12">SN</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Reg #</TableHead>
                    <TableHead className="text-right">Add</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {available.map((s, i) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(s.id)}
                          onCheckedChange={() => toggleSelect(s.id)}
                          aria-label={`Select ${s.first_name} ${s.last_name}`}
                        />
                      </TableCell>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{s.first_name} {s.last_name}</TableCell>
                      <TableCell className="font-mono text-xs">{s.student_number}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => add(s)}
                          disabled={adding}
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        {/* Current roster */}
        <div className="space-y-2">
          <Label className="text-xs">Current roster ({members.length})</Label>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">SN</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Reg #</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No members yet.</TableCell></TableRow>
              ) : members.map((m, i) => {
                const isLeader = group.leader_student_id === m.student_id;
                return (
                  <TableRow key={m.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{m.students ? `${m.students.first_name} ${m.students.last_name}` : "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{m.students?.student_number ?? "—"}</TableCell>
                    <TableCell>
                      {isLeader ? (
                        <Badge className="gap-1"><Star className="h-3 w-3" /> Leader</Badge>
                      ) : (
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setLeader(m.student_id)}>
                          <Star className="h-3 w-3 mr-1" /> Make leader
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(m)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
